import {
  IElegibility,
  InterestRateFrequency,
  ISimulationResponse,
  PreFixedTypes,
} from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2/types';
import { BaseHttpService } from './baseHttpService';

export type TSimulatorV2Request = {
  insurance_amount?: number;
  balanceCheckId?: string;
  requestedAmount?: number;
  totalAmountOwed?: number;
  paymentValue?: number;
  financedAmount?: number;
  financialIndicatorCobransaas?: ISimulationResponse;
  numPayments: number;
  tacAmount?: number;
  tacRate?: number;
  interestRate: number;
  financeFee: number;
  discountRate?: number;
  iofType: string;
  scheduleType: string;
  firstPaymentDate: string;
  disbursementDate: string;
  additionalInstallmentFee?: number | null;
  additionalInstallmentDescription?: string | null;
  amortizationFrequency?: ['MONTHLY', 'BIMONTHLY', 'SEMIANNUAL', 'ANNUAL'];
  gracePeriodInterest?: number;
  gracePeriodAmortization?: number[];
  simulationType?: string;
  interestPreType?: string;
  interestRateFrequency?: InterestRateFrequency;
  periods?: Array<{
    amount: number;
    credit_date: string;
    available: number;
  }>;
};

export type TSimulatorV2Response = {
  id?: string;
  total_processing_cost: number;
  annual_interest_rate: number;
  annual_effective_interest_rate: number;
  monthly_effective_interest_rate: number;
  iof_amount: number;
  finance_fee: number;
  insurance_amount?: number;
  interest_rate: number;
  tac_amount: number;
  discount_amount: number;
  discount_rate: number;
  total_amount_owed: number;
  financed_amount: number;
  iof_daily_rate: number;
  iof_base_rate: number;
  requested_amount: number;
  payment_amount: number;
  disbursement_amount: number;
  num_periods: number;
  first_payment_date: string;
  issue_date: string;
  disbursement_date: string;
  created_at: string;
  schedule: Array<{
    payment: number;
    iof: number;
    balance: number;
    interest: number;
    principal: number;
    additional_value: number;
    present_value: number;
    status: string;
    period: number;
    running_day: number;
    late_day: number;
    paid_value: number;
    payment_date: string;
  }>;
  schedule_type: string;
  borrower_type: string;
  interest_compounding_type: string;
  financed_iof: boolean;
  last_payment_date: string;
  amortization_frequency: string; // mensal, bimestral, semestral, etc..
  interest_pre_type: PreFixedTypes;
  interest_rate_frequency: InterestRateFrequency;
  amortization_type: string; // FLEXIBLE, PRICE, SAC ...
  grace_period_amortization: number[];
  additional_installment_fee?: number | null;
};

export type TFirstPaymentDateOption = {
  disbursement_date: string;
  transfer_date_limit: string;
};

export class SimulatorV2HttpService extends BaseHttpService {
  BASE_URL = `${process.env.REACT_APP_DASHBOARD_API_HOST}/proxy`;

  simulate = async (
    productId: string,
    params: TSimulatorV2Request,
    options?: { useNotificationOnly?: boolean },
  ) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/products/${productId}/preview`,
      {
        balance_check_id: params.balanceCheckId,
        requested_amount: params.requestedAmount,
        total_amount_owed: params.totalAmountOwed,
        payment_value: params.paymentValue,
        financed_amount: params.financedAmount,
        interest_rate: params.interestRate,
        tac_amount: params.tacAmount,
        finance_fee: params.financeFee,
        insurance_amount: params.insurance_amount,
        num_payments: params.numPayments,
        discount_rate: params.discountRate,
        first_payment_date: params.firstPaymentDate,
        disbursement_date: params.disbursementDate,
        iof_type: params.iofType,
        schedule_type: params.scheduleType,
        tac_rate: params.tacRate,
        additional_installment_fee: params.additionalInstallmentFee,
        additional_installment_description:
          params.additionalInstallmentDescription,
        financial_indicator_cobransaas: params.financialIndicatorCobransaas,
        amortization_type: params.simulationType,
        amortization_frequency: params.amortizationFrequency,
        grace_period_interest: params.gracePeriodInterest,
        grace_period_amortization: params.gracePeriodAmortization || [],
        interest_pre_type: params.interestPreType,
        interest_rate_frequency: params.interestRateFrequency,
        periods: params.periods,
      },
      options?.useNotificationOnly ? { headers: { handled: true } } : undefined,
    );
    return res;
  };

  getDownloadSimulation = async (id: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/receipt-guarantee-balance/download?id=${id}`,
    );

    return res.data;
  };

  getElegibilityRules = async (
    productId: string,
  ): Promise<IElegibility | null> => {
    const res = await this.get(
      `${this.BASE_URL}/originator/products/${productId}/eligibilities`,
    );
    return res.data;
  };

  getFirstPaymentDates = async (
    productId: string,
    disbursementDate: string,
  ): Promise<TFirstPaymentDateOption[]> => {
    const res = await this.get(
      `${this.BASE_URL}/originator/${productId}/first-payment-dates?disbursementDate=${disbursementDate}`,
    );
    return res.data;
  };
}
